<template>
  <div class="flex flex-col h-[calc(100vh-52px)] relative min-h-0 overflow-y-auto">
    <div
      v-if="isSidepaneLoading"
      class="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75 rounded-lg"
    >
      <div class="flex flex-col items-center gap-3">
        <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-primary"></div>
        <span class="font-medium text-gray-600">Updating order...</span>
      </div>
    </div>
    <div
      class="fixed top-2 right-2 md:top-[60px] md:right-5 z-[10002] flex items-center gap-2 bg-[#f3f3f3]"
    >
      <button
        type="button"
        @click="emits('onClose')"
        aria-label="Close order details"
        class="flex justify-center items-center p-2 text-white bg-gray-400 rounded-full cursor-pointer hover:bg-gray-600 border-0"
      >
        <XMarkIcon class="text-white size-5" />
      </button>
    </div>

    <div ref="headerContainer" class="static md:sticky top-0 z-[31] pt-4 bg-[#f3f3f3]">
      <div class="border-b pb-[11px] mb-2">
        <div class="flex flex-wrap gap-3 md:gap-5 p-3 pr-12 md:pr-3">
          <!--
            A vertical stack: the identity line, then whatever badges apply. Everything used
            to be inline siblings in one div, so the "Change order type" button ended up
            butted against the customer name with no gap, and moved around depending on
            whether the remake badges happened to render.
          -->
          <div class="flex flex-col gap-1.5">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span v-if="headerOrderNumberLabel" class="font-bold text-sm">
                ORDER# {{ headerOrderNumberLabel }}
              </span>
              <span class="font-bold text-sm" v-else>Order pending confirmation</span>

              <ComplaintStatusPill
                v-if="orderComplaints.length > 0"
                :complaints="orderComplaints"
                :clickable="true"
                @pill-click="scrollToLatestComplaint"
              />

              <span class="text-sm font-light capitalize">{{ cleanName(selectedOrderDetails?.customer_name) ? selectedOrderDetails?.customer_name : "N/A" }}</span>

              <!--
                AB#20569 / AB#20571 — changing the type as its own action, sitting on the
                identity line rather than under it.

                Order type used to be editable only inside the price breakdown modal, which
                made reclassifying an order and repricing it one save, and opened the remake
                intake questionnaire from inside a pricing dialog. This is the separated
                action. The price modal still offers it for now; removing that changes an
                existing admin workflow and is its own piece of work.

                can() only decides whether the button is offered. The route carries
                routeHasPermission and orderTypeChangeAccess, and the service refuses a role
                config('custimoo.order_type_change') does not admit — a hidden button is
                never the only protection.
              -->
              <button
                v-if="can('change-order-type')"
                type="button"
                class="whitespace-nowrap rounded-md border border-[#00b087] px-2.5 py-1 text-xs font-medium leading-none text-[#00b087] transition-colors hover:bg-[#00b087] hover:text-white"
                @click="showChangeOrderType = true"
              >
                Change order type
              </button>
            </div>

            <!--
              AB#20578 — which order this remake replaces, and who owned it.

              This is the question AB#20569 exists to answer: for an order flagged as a
              remake there was previously no way at all to find the original order or its
              Order Administrator. Only rendered when there is a link, so ordinary orders
              are unaffected.

              The three admin states are stated rather than collapsed into a blank: a null
              Order Administrator is ambiguous between "nobody was ever assigned" and "the
              person has left", and those need different follow-up.
            -->
            <div v-if="originalOrderInfo" class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span class="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">
                {{ originalOrderInfo.label }} of #{{ originalOrderInfo.orderNo }}
              </span>
              <span class="text-gray-500">
                Original Order Admin:
                <span v-if="originalOrderInfo.adminName" class="font-medium text-gray-700">
                  {{ originalOrderInfo.adminName }}
                </span>
                <span v-else-if="originalOrderInfo.adminHasLeft" class="font-medium text-red-600">
                  no longer active
                </span>
                <span v-else class="font-medium text-red-600">never assigned</span>
              </span>
            </div>

            <!--
              The other direction. Standing on the ORIGINAL order there was previously nothing
              to say it had ever been remade, which is the side a complaint usually starts
              from. More than one is possible: a remake that fails gets remade again.
            -->
            <div v-if="derivedOrdersInfo.length" class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span class="text-gray-500">Replaced by:</span>
              <span
                v-for="derived in derivedOrdersInfo"
                :key="derived.orderNo"
                class="rounded bg-sky-100 px-1.5 py-0.5 font-medium text-sky-900"
              >
                {{ derived.label }} #{{ derived.orderNo }}
              </span>
            </div>
          </div>
          <div class="flex flex-wrap gap-3">
            <div ref="selectContainer" class="w-full md:w-auto" v-if="selectedRow?.factory_id">
              <Select
                v-if="can('assign-order-administrator') && selectedRow?.factory_id"
                :options="
                  orderAdmins.filter((admin: OrderAdminUser) => {
                    // If admin has 'leadorderAdministrator' role, include them without factory_id filtering
                    // Format role names by removing spaces for comparison
                    const formattedRoleNames = admin.role_names?.map((role) =>
                      role.replace(/\s+/g, '')
                    );
                    if (
                      formattedRoleNames?.some(
                        (role) => role.toLowerCase() === 'leadorderadministrator'
                      )
                    ) {
                      return true;
                    }
                    // Otherwise, filter by factory_id
                    return admin.factory_ids?.includes((selectedRow as DashboardItem).factory_id);
                  })
                "
                :modelValue="selectedRow.order.order_administrator"
                @update:modelValue="onAssignAdmin"
                track-by="id"
                label="name"
                classProps="assign-admins-button w-full md:w-auto min-w-[150px] md:max-w-max text-sm"
                placeholder="Primary Admin"
                append-to-body
              />
            </div>

            <div
              class="w-full md:w-auto"
              v-if="can('assign-order-administrator') && selectedRow?.factory_id"
            >
              <Select
                multiple
                append-to-body
                :options="filteredOrderAdmins"
                :modelValue="selectedSecondaryAdmins"
                @update:modelValue="onAssignSecondaryAdmins"
                placeholder="Secondary Admins"
                classProps="assign-admins-button w-full md:w-auto min-w-[170px] max-w-full md:max-w-[300px] text-sm"
                track-by="id"
                label="name"
              >
                <template #option="{ name }">
                  <p style="margin: 0">
                    {{ name }}
                  </p>
                </template>
              </Select>
            </div>
          </div>
        </div>
        <div class="flex w-full md:w-max items-center gap-2 flex-wrap p-3">
          <!-- Customer Reference Number -->
          <div class="flex text-sm" v-if="!isNullOrEmpty(customerReferenceNo)">
            <span class="font-bold">Reference:</span>
            <span class="pl-1 text-sm">{{ customerReferenceNo }}</span>
          </div>

          <!-- Factory PO Number -->
          <div class="flex text-sm items-center">
            <span class="font-bold">PO Num:</span>
            <span class="pl-1">{{ factoryPoNumber }}</span>

            <!-- PO File Download Link -->
            <template v-if="poFileInfo">
              <span class="font-bold pl-2">PO Document:</span>
              <a
                :href="poFileDownloadUrl"
                :download="poFileName"
                class="ml-2 text-blue-600 hover:text-blue-800 cursor-pointer flex items-center gap-1"
                :title="`Download PO: ${poFileName}`"
              >
                <span class="text-sm">Open File</span>
              </a>
            </template>
          </div>
          <div
            class="flex text-sm items-center gap-2"
            v-if="
              (can('display-csManager') && selectedOrderDetails?.cs_manager) ||
              (can('display-salesRepresentative') && selectedOrderDetails?.sales_representative)
            "
          >
            <span
              v-if="can('display-csManager') && selectedOrderDetails?.cs_manager"
              class="text-sm"
            >
              <strong>CSM: </strong>{{ selectedOrderDetails.cs_manager?.name }}
            </span>

            <span
              v-if="
                can('display-salesRepresentative') && selectedOrderDetails?.sales_representative
              "
              class="text-sm"
            >
              <strong>SR: </strong>{{ selectedOrderDetails.sales_representative?.name }}
            </span>
          </div>

          <!-- Dispatch method badge -->
          <button
            v-if="selectedOrderDetails?.dispatch_method === 'auto'"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300 cursor-pointer hover:bg-green-200 transition-colors"
            title="Click to view auto-dispatch audit log"
            @click="showDispatchAudit = true"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
            Auto-dispatched
          </button>
          <button
            v-else-if="selectedOrderDetails?.latest_dispatch_evaluation"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 cursor-pointer hover:bg-amber-200 transition-colors"
            title="Click to view why auto-dispatch was not triggered"
            @click="showDispatchAudit = true"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>
            {{ selectedOrderDetails.latest_dispatch_evaluation.final_decision === 'toggle_off' ? 'Auto-dispatch off' : 'Manual review required' }}
          </button>
          <span
            v-else-if="selectedOrderDetails?.dispatch_method === 'manual'"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"></span>
            Manually dispatched
          </span>
        </div>

        <div
          v-if="orderCompositeInvoiceLabel || orderStripeInvoicesForHeader.length"
          class="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-3 pb-2 text-sm border-t border-gray-200/80 mt-1 pt-2"
        >
          <div v-if="orderCompositeInvoiceLabel" class="flex items-center gap-1.5">
            <span class="font-bold">Billing:</span>
            <span :class="orderCompositeInvoicePillClass">
              {{ orderCompositeInvoiceLabel }}
            </span>
          </div>
          <div
            v-if="orderStripeInvoicesForHeader.length"
            class="flex flex-wrap items-center gap-x-3 gap-y-1"
          >
            <span class="font-bold shrink-0">Invoices:</span>
            <span
              v-for="inv in orderStripeInvoicesForHeader"
              :key="String(inv.stripe_invoice_id ?? inv.id)"
              class="contents"
            >
              <a
                v-if="stripeInvoicePdfUrl(inv)"
                :href="stripeInvoicePdfUrl(inv) || ''"
                target="_blank"
                rel="noopener noreferrer"
                :download="stripeInvoicePdfDownloadName(inv)"
                class="text-[#219f84] hover:underline text-xs cursor-pointer"
                :title="
                  'Download PDF' +
                  (inv.stripe_invoice_number ? ` (${inv.stripe_invoice_number})` : '')
                "
              >
                {{ inv.stripe_invoice_number || inv.stripe_invoice_id
                }}<span v-if="inv.status" class="text-gray-500"> ({{ inv.status }})</span>
              </a>
              <span
                v-else
                class="text-xs text-gray-600"
                :title="'PDF not stored yet — refresh after sync'"
              >
                {{ inv.stripe_invoice_number || inv.stripe_invoice_id
                }}<span v-if="inv.status" class="text-gray-500"> ({{ inv.status }})</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Auto-dispatch audit drawer -->
    <Teleport to="body">
      <div
        v-if="showDispatchAudit"
        class="fixed inset-0 z-[10010] flex items-center justify-center"
        @click.self="showDispatchAudit = false"
      >
        <div class="absolute inset-0 bg-black/40"></div>
        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 flex flex-col max-h-[90vh]">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                {{ selectedOrderDetails?.dispatch_method === 'auto' ? 'Auto-dispatch audit log' : 'Dispatch evaluation log' }}
              </h3>
              <p v-if="selectedOrderDetails?.latest_dispatch_evaluation" class="text-xs text-gray-400 mt-0.5">
                Evaluated: {{ formatDate(selectedOrderDetails.latest_dispatch_evaluation.evaluated_at) }}
                &nbsp;·&nbsp;
                <span
                  class="font-medium"
                  :class="selectedOrderDetails.latest_dispatch_evaluation.eligible ? 'text-green-600' : 'text-red-500'"
                >{{ selectedOrderDetails.latest_dispatch_evaluation.eligible ? 'All conditions passed' : 'Some conditions failed' }}</span>
              </p>
            </div>
            <button
              @click="showDispatchAudit = false"
              class="text-gray-400 hover:text-gray-600 transition-colors shrink-0 ml-4"
            >
              <XMarkIcon class="size-5" />
            </button>
          </div>

          <!-- Body -->
          <div class="overflow-y-auto px-6 py-4 flex-1">
            <template v-if="selectedOrderDetails?.latest_dispatch_evaluation">
              <!-- Conditions list -->
              <div class="space-y-2">
                <div
                  v-for="(condition, idx) in auditConditions"
                  :key="idx"
                  class="flex items-start gap-3 p-3 rounded-lg border"
                  :class="condition.passed
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'"
                >
                  <!-- Icon -->
                  <span
                    class="shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
                    :class="condition.passed ? 'bg-green-500 text-white' : 'bg-red-500 text-white'"
                  >{{ condition.passed ? '✓' : '✗' }}</span>
                  <!-- Text -->
                  <div class="min-w-0">
                    <p class="text-sm font-medium" :class="condition.passed ? 'text-green-800' : 'text-red-800'">
                      {{ condition.label }}
                    </p>
                    <p class="text-xs mt-0.5" :class="condition.passed ? 'text-green-600' : 'text-red-600'">
                      {{ condition.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Manual review reason: only show when no conditions data available (fallback) -->
              <div
                v-if="selectedOrderDetails?.manual_review_reason && !auditConditions.length"
                class="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <p class="text-xs font-semibold text-amber-700 mb-1">Reason flagged for manual review:</p>
                <div class="flex flex-wrap gap-1.5">
                  <span
                    v-for="reason in selectedOrderDetails.manual_review_reason.split(',')"
                    :key="reason"
                    class="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-mono"
                  >{{ humanizeConditionName(reason.trim()).label }}</span>
                </div>
              </div>
            </template>
            <p v-else class="text-sm text-gray-500">No evaluation data available for this order.</p>
          </div>
        </div>
      </div>
    </Teleport>
    <Tabs
      v-if="selectedOrderDetails"
      :tabs="filteredOrderDetailTabs"
      :initial-tab="initialTab"
      :key="selectedRow?.id"
      @change="(tabId: string) => emits('detailsTabChange', tabId)"
      sticky
      sticky-class="static md:sticky z-30 bg-[#f3f3f3] overflow-hidden dynamic-sticky-tabs"
    >
      <!--
        AB#21202 — ELSA's face, supplied by the caller so Tabs.vue stays a tab
        strip and does not learn what an agent is. `ground` matches whatever the
        pill is sitting on, so the avatar's circle never shows a white halo.
      -->
      <template #tab-prefix="{ tab }">
        <span v-if="tab.id === 'ask_elsa'" class="elsa-avatar-slot">
          <!--
            Transparent ground: the soft disc behind the face belongs to the
            pill, which changes with the pill's own state, so the avatar must
            not paint a circle of its own over it.
          -->
          <ElsaAvatar ground="transparent" />
        </span>
      </template>
      <!--
        The freshness dot (AB#21202).

        Amber once the stored reading has events behind it. Absent until the tab
        has been opened at least once for this order — AskElsaTab only mounts
        when its slot is rendered, so before that nobody has asked the API and
        the honest thing is to show nothing rather than to imply "fresh".
      -->
      <template #tab-suffix="{ tab }">
        <span
          v-if="tab.id === 'ask_elsa' && elsaReadIsStale"
          class="tabs-tab-dot elsa-stale-dot"
          data-test="elsa-stale-dot"
          title="ELSA's read is older than this order's latest event"
        ></span>
      </template>
      <template #actions>
        <div class="flex justify-center items-center px-4 border-b mt-0 overflow-y-auto">
          <OrderDetailActions
            ref="orderActions"
            :current-tab="currentTab"
            :order_item_list="selectedRow"
            :order_item_price="selectedRow.order?.price_info?.order_final_price"
            :order="selectedOrderDetails"
            @onFetch="() => emits('fetchOnSave', true)"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
          />
        </div>
      </template>
      <template #timeline>
        <div class="p-5 overflow-y-auto mt-5 space-y-4">
          <FeedbackPanel :order-id="selectedOrderDetails?.id" />
          <OrderTimeline
            :order="selectedOrderDetails"
            :order_item="currentOrderItem"
            :order_item_index="currentOrderItemIndex"
            :order_no="selectedOrderDetails.order_no"
            :isLoading="isSidepaneLoading"
            :current-tab="currentTab"
            :selectedRow="selectedRow"
            @onFetch="() => emits('fetchOnSave', true)"
            @orderMoved="emits('handleOrderMoved')"
            @orderUpdated="emits('handleOrderUpdated')"
          />
        </div>
      </template>
      <template #detail>
        <div class="p-5 overflow-y-auto mt-0">
          <FactoryProductsTab
            v-if="currentOrderItem?.factory_products?.length"
            :factoryProducts="JSON.parse(JSON.stringify(currentOrderItem?.factory_products))"
            :order="selectedOrderDetails"
            :order_item_id="currentOrderItem?.id"
            :order-item="currentOrderItem"
            :order_item_price="selectedRow.price_info?.order_item_final_price"
            :order_line="currentOrderItem?.order_line ?? null"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            :key="JSON.stringify(currentOrderItem?.factory_products)"
            :copiedRoster="copiedRoster"
            @onCopyRoster="copyRoster"
          />
          <p class="text-gray-700" v-else>No factory products available.</p>
        </div>
      </template>
      <template #files>
        <div class="p-5 overflow-y-auto mt-0">
          <div v-if="currentOrderItem?.factory_id" class="p-5 border border-gray-200 rounded-lg">
            <h2 class="text-xl font-semibold mb-3">Files Content</h2>
            <OrderFiles
              :order="currentOrderItem"
              :order-id="currentOrderItem.order_id"
              :order-factories="[
                {
                  id: currentOrderItem.factory_id,
                  name: currentOrderItem.factory_name,
                },
              ]"
              @show-logs="showFileLogs"
              @loading-logs="toggleFileLogsLoading"
            />
          </div>
          <div
            v-else
            class="p-5 border border-gray-200 rounded-lg flex items-center w-full justify-center bg-white"
          >
            <EmptyState title="Oops!" message="Please assign a factory to view files.." />
          </div>
          <SharePointActivityLogs
            :showActivitiesPanel="sharepointLogsVisible"
            :isSharepointLogsLoading="isSharepointLogsLoading"
            :sharePointActivityLogsObject="sharepointActivityLogsObject"
            @close-sharepoint-activity-panel="closeSharepointActivityLog"
          >
          </SharePointActivityLogs>
        </div>
      </template>
      <template #activity_logs>
        <div class="p-5 overflow-y-auto mt-10 md:mt-0 flex flex-col min-h-0 flex-1">
          <OrderActivityLogsTab
            :order-id="selectedOrderDetails.id"
            :order-item-id="selectedRow?.id"
            :row-key="selectedRow?.id"
          />
        </div>
      </template>
      <!--
        AB#21202 — Ask ELSA. Keyed on the order item so switching orders remounts
        it: an analysis is about one specific order, and the one thing this tab
        must never do is show the previous order's reading under the current
        order's number.
      -->
      <template #ask_elsa>
        <div class="overflow-y-auto mt-10 md:mt-0 flex flex-col min-h-0 flex-1">
          <AskElsaTab
            v-if="selectedRow?.id"
            :key="selectedRow.id"
            :order-item-id="selectedRow.id"
            @staleness="(stale: boolean) => (elsaReadIsStale = stale)"
            @act="onElsaAction"
          />
        </div>
      </template>
    </Tabs>
  </div>


  <ChangeOrderTypeModal
    :show="showChangeOrderType"
    :order-no="selectedRow?.order?.order_no ?? selectedRow?.order?.id ?? ''"
    :current-symbol="selectedRow?.order?.order_type_symbol ?? null"
    @close="showChangeOrderType = false"
    @saved="emitOrderTypeChanged"
  />
</template>
<script setup lang="ts">
import ComplaintStatusPill from '@/components/Dashboard/Order/components/ComplaintStatusPill.vue';
import FeedbackPanel from '@/components/Dashboard/Order/components/FeedbackPanel.vue';
import FactoryProductsTab from '@/components/Dashboard/Order/components/FactoryProductsTab.vue';
import OrderTimeline from '@/components/Dashboard/Order/OrderTimeline.vue';
import EmptyState from '@/components/Shared/EmptyState.vue';
import Select from '@/components/Shared/Select.vue';
import ChangeOrderTypeModal from '@/components/Dashboard/Modals/ChangeOrderTypeModal.vue';
import Tabs from '@/components/Shared/Tabs.vue';
import { can, cleanName, getComplaintsArray, isNullOrEmpty } from '@/composables';
import { DashboardItem, OrderAdminUser, OrderDetail, OrderDetailItem } from '@/models';
import { CopiedRoster } from '@/models/CopiedRoster';
import { SharepointActivityLogsObject } from '@/models/SharepointActivityLog';
import { useMainStore } from '@/store';
import { invoiceCompositeStatusPillClass } from '@/utils/invoiceCompositeStatusDisplay';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AskElsaTab from './components/AskElsaTab.vue';
import ElsaAvatar from './components/ElsaAvatar.vue';
import OrderActivityLogsTab from './components/OrderActivityLogsTab.vue';
import OrderDetailActions from './components/OrderDetailActions.vue';
import SharePointActivityLogs from './components/SharePointActivityLogs.vue';
import OrderFiles from './OrderFiles.vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

interface Tab {
  id: string;
  label: string;
}
const { updateOrderAdmin } = useMainStore();
interface Props {
  currentTab?: string;
  selectedOrderDetails: OrderDetail;
  isSidepaneLoading: boolean;
  selectedRow: DashboardItem;
  filteredOrderDetailTabs: Tab[];
  currentOrderItem: OrderDetailItem | null;
  currentOrderItemIndex: number | null;
  sidebarOpen: boolean;
  initialTab: string;
  orderAdmins: OrderAdminUser[];
}

const props = defineProps<Props>();
const emits = defineEmits([
  'fetchOnSave',
  'handleOrderMoved',
  'handleOrderUpdated',
  'openOrderPriceModal',
  'detailsTabChange',
  'onClose',
]);
/**
 * After a type change the order's type, its original-order link and its timeline have all
 * moved, so the list has to be refetched rather than patched locally — the change also
 * writes an activity log entry and a comment, neither of which this component owns.
 *
 * Uses the existing `fetchOnSave` channel that DashboardActions already listens to, instead
 * of reaching into the store: getDashboardData() needs the current DashboardRequest, and
 * this component does not have it.
 */
function emitOrderTypeChanged(): void {
  emits('fetchOnSave', true);
}

const selectContainer = ref<HTMLElement | null>(null);
const headerContainer = ref<HTMLElement | null>(null);
const showDispatchAudit = ref(false);

// ─── Auto-dispatch audit helpers ────────────────────────────────────────────

const CONDITION_META: Record<string, { label: string; description: string }> = {
  // Pass codes (from RuleResult::pass)
  merchant_setting_enabled: {
    label: 'Auto-dispatch enabled',
    description: 'Auto-dispatch is turned on for this merchant.',
  },
  original_order_completed: {
    label: 'Original order completed',
    description: 'The original order was fully completed before this reorder.',
  },
  no_open_issues:  {
    label: 'No open issues',
    description: 'No unresolved comments on the original order item.',
  },
  product_design_match: {
    label: 'Product & design match',
    description: 'Product, design, colors and logo placements are identical to the original.',
  },
  factory_active: {
    label: 'Factory / supplier active',
    description: 'The original factory exists and has at least one active user.',
  },
  roster_file_present: {
    label: 'New roster file uploaded',
    description: 'A new roster file has been provided for this reorder.',
  },
  // Fail codes (from RuleResult::fail)
  merchant_setting_disabled: {
    label: 'Auto-dispatch disabled',
    description: 'Auto-dispatch is turned off for this merchant. Enable it in Settings.',
  },
  original_not_completed: {
    label: 'Original order not completed',
    description: 'The original order was never marked as completed.',
  },
  original_has_unresolved_comment: {
    label: 'Open issues on original order',
    description: 'There are unresolved comments on the original order item.',
  },
  original_link_missing: {
    label: 'Original order link missing',
    description: 'Could not find the original order item this reorder refers to.',
  },
  product_mismatch: {
    label: 'Product mismatch',
    description: 'The product type or design has changed from the original.',
  },
  design_mismatch: {
    label: 'Design mismatch',
    description: 'The design ID does not match the original order.',
  },
  svg_groups_mismatch: {
    label: 'Logo placements changed',
    description: 'SVG groups (logo positions / placements) differ from the original.',
  },
  factory_inactive_or_missing_user: {
    label: 'Factory / supplier inactive',
    description: 'The original factory is inactive or has no active user accounts.',
  },
  factory_mismatch: {
    label: 'Factory mismatch',
    description: 'The current order is already assigned to a different factory.',
  },
  roster_file_missing: {
    label: 'No new roster file',
    description: 'Roster change was not checked. Please upload a new roster file.',
  },
  assignment_failed: {
    label: 'Assignment error',
    description: 'An unexpected error occurred while trying to assign the factory.',
  },
}

function humanizeConditionName(code: string): { label: string; description: string } {
  return CONDITION_META[code] ?? {
    label: code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    description: '',
  }
}

/** Full ordered condition list derived from the latest evaluation. */
const auditConditions = computed(() => {
  const evaluation = props.selectedOrderDetails?.latest_dispatch_evaluation
  if (!evaluation) return []

  return (evaluation.conditions ?? []).map((c: { name: string; passed: boolean }) => ({
    ...humanizeConditionName(c.name),
    passed: c.passed,
  }))
})

/*
 * Ask ELSA's two wires into the rest of the page (AB#21202).
 *
 * `elsaReadIsStale` drives the dot on the tab. It is reported upward by the tab
 * rather than fetched here on purpose: the analysis endpoint is a real query,
 * and firing it for every order anybody opens — whether or not they ever look
 * at the tab — is a cost the dot does not justify. The consequence is that the
 * dot is absent until the tab has been opened once for this order, which is the
 * honest state: nobody has asked.
 *
 * `onElsaAction` lets the read card's "do this next" buttons open the same
 * modals the action row opens, instead of telling a CSM to go and find the
 * button themselves. Reset when the selected order changes, or a stale dot
 * would follow the reader onto the next order.
 */
const elsaReadIsStale = ref(false);
const orderActions = ref<InstanceType<typeof OrderDetailActions> | null>(null);

const onElsaAction = (action: 'internal-comment' | 'support-threads'): void => {
  if (action === 'internal-comment') {
    orderActions.value?.openInternalNote?.();

    return;
  }

  orderActions.value?.openSupportThreads?.();
};

watch(
  () => props.selectedRow?.id,
  () => {
    elsaReadIsStale.value = false;
  }
);

// Computed properties for header fields
/** First order item’s `item_no` when present; otherwise `order_no`. */
const headerOrderNumberLabel = computed(() => {
  const items = props.selectedOrderDetails?.items;
  const itemNo = items?.[0]?.item_no;
  if (itemNo != null && String(itemNo).trim() !== '') {
    return String(itemNo).trim();
  }
  const orderNo = props.selectedOrderDetails?.order_no;
  if (orderNo != null && String(orderNo).trim() !== '') {
    return String(orderNo).trim();
  }
  return '';
});

const customerReferenceNo = computed(() => {
  return props.selectedOrderDetails?.customer_reference_no || '';
});

const factoryPoNumber = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_number || 'N/A';
});

const poFileInfo = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_file_info;
});

const poFileDownloadUrl = computed(() => {
  if (!poFileInfo.value) return '';
  const BASE_URL = import.meta.env.VITE_APP_FILE_URL;
  return `${BASE_URL}/${poFileInfo.value.path}`;
});

const poFileName = computed(() => {
  if (!poFileInfo.value) return '';
  return poFileInfo.value.name || poFileInfo.value.filename || 'PO_Document';
});

/** Raw composite key for the selected line item (for pill colors). */
const orderItemCompositeStatusRaw = computed(
  () => (props.selectedRow as DashboardItem | undefined)?.invoice_composite_status
);

const orderCompositeInvoicePillClass = computed(() =>
  invoiceCompositeStatusPillClass(
    orderItemCompositeStatusRaw.value == null ||
      String(orderItemCompositeStatusRaw.value).trim() === ''
      ? null
      : String(orderItemCompositeStatusRaw.value)
  )
);

/** Composite billing status for the selected line item (`order_items.invoice_composite_status`). */
const orderCompositeInvoiceLabel = computed(() => {
  const row = props.selectedRow as DashboardItem | undefined;
  const s = row?.invoice_composite_status;
  return s != null && String(s).trim() !== '' ? String(s).replace(/_/g, ' ') : '';
});

/** Deduped Stripe invoices from all line items (`item_invoices`). */
const orderStripeInvoicesForHeader = computed((): Record<string, unknown>[] => {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  const items = props.selectedOrderDetails?.items ?? [];
  for (const it of items) {
    const row = it as unknown as Record<string, unknown>;
    const raw = row.item_invoices ?? row.itemInvoices;
    if (!Array.isArray(raw)) continue;
    for (const inv of raw) {
      const rec = inv as Record<string, unknown>;
      const sid = rec.stripe_invoice_id;
      if (sid == null || sid === '') continue;
      const key = String(sid);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rec);
    }
  }
  return out;
});

/** Stripe-hosted PDF URL on `order_item_invoices.invoice_pdf` — opens/downloads PDF, not the payment page. */
function stripeInvoicePdfUrl(inv: Record<string, unknown>): string | null {
  const pdf = inv?.invoice_pdf;
  if (typeof pdf === 'string' && pdf.trim() !== '') {
    return pdf.trim();
  }
  return null;
}

function stripeInvoicePdfDownloadName(inv: Record<string, unknown>): string {
  const num = inv?.stripe_invoice_number;
  const base =
    typeof num === 'string' && num.trim() !== ''
      ? num.trim().replace(/[^\w.-]+/g, '_')
      : String(inv?.stripe_invoice_id ?? 'invoice');
  return `${base}.pdf`;
}

const showChangeOrderType = ref(false);

/**
 * AB#20578 — the original order behind a remake, and its Order Administrator.
 *
 * Returns null when there is no link, which is the normal case: only remakes and reorders
 * have one, and orders created before AB#20572 have none until the backfill runs
 * (php artisan orders:backfill-original-order-id).
 *
 * adminHasLeft distinguishes the two ways the administrator can be missing. The API sends
 * original_order.order_administrator_id alongside the relation precisely so this is
 * answerable: an id with no object means the user is soft-deleted, i.e. they have left.
 */
const originalOrderInfo = computed<{
  orderNo: string | number;
  label: string;
  adminName: string | null;
  adminHasLeft: boolean;
} | null>(() => {
  const order = props.selectedRow?.order as Record<string, any> | undefined;
  const original = order?.original_order;
  if (!original) return null;

  const admin = original.order_administrator ?? null;

  return {
    orderNo: original.order_no ?? original.id,
    // Read off THIS order's type, not a hardcoded "Remake": a reorder carries the same link
    // and calling it a remake on screen would misreport the remake rate to anyone reading it.
    label: linkLabel(order?.order_type_symbol),
    adminName: admin?.name ?? null,
    adminHasLeft: !admin && Boolean(original.order_administrator_id),
  };
});

/** 'Ri'/'R' are remakes, 'REORDER' a reorder; anything else only that it replaces something. */
function linkLabel(symbol?: string | null): string {
  if (symbol === 'Ri' || symbol === 'R') return 'Remake';
  if (symbol === 'REORDER') return 'Reorder';
  return 'Replacement';
}

/** The remakes and reorders made FROM this order, newest information first on screen. */
const derivedOrdersInfo = computed<{ orderNo: string | number; label: string }[]>(() => {
  const derived = (props.selectedRow?.order as Record<string, any> | undefined)?.derived_orders;
  if (!Array.isArray(derived)) return [];

  return derived.map((d: Record<string, any>) => ({
    orderNo: d.order_no ?? d.id,
    label: linkLabel(d.order_type_symbol),
  }));
});

const filteredOrderAdmins = computed(() => {
  return props.orderAdmins.filter((admin: OrderAdminUser) => {
    // If admin has 'leadorderAdministrator' role, include them without factory_id filtering
    // Format role names by removing spaces for comparison
    const formattedRoleNames = admin.role_names?.map((role) => role.replace(/\s+/g, ''));
    if (formattedRoleNames?.some((role) => role.toLowerCase() === 'leadorderadministrator')) {
      return true;
    }
    // Otherwise, filter by factory_id
    return admin.factory_ids?.includes((props.selectedRow as DashboardItem).factory_id);
  });
});

const selectedSecondaryAdmins = computed(() => {
  return (
    props.selectedRow?.order?.secondary_order_administrators
      ?.map((item) => item.user)
      .filter(Boolean) || []
  );
});

const orderComplaints = computed(() => getComplaintsArray(props.selectedOrderDetails));

const scrollToLatestComplaint = () => {
  const latest = orderComplaints.value[0];
  if (!latest) return;
  const cid = latest.id ?? latest.ticket_no ?? '';
  if (!cid) return;
  emits('detailsTabChange', 'timeline');
  nextTick(() => {
    setTimeout(() => {
      const el = document.getElementById(`complaint-card-${cid}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
  });
};

const copiedRoster = ref<CopiedRoster>({
  isAnyRosterCopied: false,
  copiedFromProductIndex: null,
  rosterData: [],
});
const sharepointActivityLogsObject = ref<SharepointActivityLogsObject>(null);
const sharepointLogsVisible = ref<Boolean>(false);
const isSharepointLogsLoading = ref(false);

const onAssignAdmin = async (newValue: OrderAdminUser) => {
  const secondaryAdminIds = [];
  const secondaryAdmin =
    props.selectedRow?.order?.secondary_order_administrators
      ?.map((item) => item.user)
      .filter(Boolean) || [];
  if (Array.isArray(secondaryAdmin) && secondaryAdmin.length) {
    secondaryAdminIds.push(...secondaryAdmin.map((u) => u.id));
  }

  const payload = {
    user_id: newValue ? newValue.id : null,
    order_ids: props.selectedRow.order.id,
    secondary_administrator_ids: secondaryAdminIds,
  };
  await updateOrderAdmin(payload);
  emits('fetchOnSave', true);
};

const onAssignSecondaryAdmins = async (newValues: OrderAdminUser[]) => {
  const secondaryAdmin = newValues;
  // selectedSecondaryOrderAdmin.value = newValues;

  const secondaryAdminIds = secondaryAdmin.map((user) => user.id);

  const payload = {
    user_id: props.selectedRow.order.order_administrator
      ? props.selectedRow.order.order_administrator.id
      : null,
    order_ids: props.selectedRow.order.id,
    secondary_administrator_ids: secondaryAdminIds,
  };
  await updateOrderAdmin(payload);
  emits('fetchOnSave', true);
};

const copyRoster = (data) => {
  copiedRoster.value.isAnyRosterCopied = data.copiedRoster.isAnyRosterCopied;
  copiedRoster.value.copiedFromProductIndex = data.copiedRoster.copiedFromProductIndex;
  copiedRoster.value.rosterData = data.copiedRoster.rosterData;
};
const showFileLogs = async (fileLogsResult: SharepointActivityLogsObject) => {
  sharepointActivityLogsObject.value = fileLogsResult;
};

const toggleFileLogsLoading = () => {
  sharepointLogsVisible.value = true;
  isSharepointLogsLoading.value = !isSharepointLogsLoading.value;
};

const closeSharepointActivityLog = () => {
  sharepointLogsVisible.value = false;
};

const getFileName = (action) => {
  const key = Object.keys(action)[0];
  const obj = action[key];
  // pick correct filename field
  return obj.oldName || obj.name || 'N/A';
};
const formatDate = (date) => {
  return new Date(date).toLocaleString();
};
const getActionType = (action) => {
  // returns the key (rename, delete, etc.)
  return Object.keys(action)[0];
};
onMounted(async () => {
  await nextTick();
  updateStickyOffset();
  window.addEventListener('resize', updateStickyOffset);
});
watch(
  () => props.selectedRow?.factory_id,
  async (factoryId) => {
    if (!factoryId) return;

    await nextTick();
    updateStickyOffset();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStickyOffset);
});

const updateStickyOffset = () => {
  const select = selectContainer.value;
  const header = headerContainer.value;
  if (header && select && window.innerWidth < 768) {
    document.documentElement.style.setProperty(
      '--dynamic-header-height',
      `${header.offsetHeight + select.offsetHeight - 5}px`
    );
  } else {
    document.documentElement.style.setProperty(
      '--dynamic-header-height',
      `${header.offsetHeight}px`
    );
  }
};
</script>
<style>
.dynamic-sticky-tabs {
  top: var(--dynamic-header-height);
}
.v-select.vs--multiple .vs__dropdown-toggle .vs__selected-options .vs__selected {
  font-size: 11px !important;
}

.assign-admins-button {
  background: white;
}
</style>
